import React, { useState, useMemo } from 'react';
import { cn, caseInsensitiveSort } from '@/lib/utils';
import { __resolve } from '@/lib/i18n';
import { Input } from '../Input/Input';
import { Button } from '../Button/Button';
// Removed Select import since we're using native select elements

export interface TableColumn<T = any> {
  key: string;
  title: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  sortable?: boolean;
  /**
   * Optional sort key extractor. When provided, the sort logic uses this value
   * instead of `row[column.key]`. Useful for computed columns (e.g. sort by
   * `name || email`, or by a nested field like primary role label).
   *
   * @example
   * { key: 'user', sortKey: (r) => (r.name || r.email).toLowerCase() }
   */
  sortKey?: (row: T) => string | number | null | undefined;
  searchable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableFilter {
  key: string;
  label: string;
  options: { label: string; value: string }[];
}

export interface TableProps<T = any> {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  filters?: TableFilter[];
  pagination?: {
    pageSize?: number;
    showSizeChanger?: boolean;
    pageSizeOptions?: number[];
  } | false;
  className?: string;
  emptyText?: string;
  // Server-side props
  serverSide?: boolean;
  onSearch?: (term: string) => void;
  onFilter?: (filters: Record<string, string>) => void;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  onPaginate?: (page: number, pageSize: number) => void;
  totalItems?: number;
}

export function Table<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  searchable = true,
  searchPlaceholder = __resolve('ui::Search...'),
  filters = [],
  pagination = { pageSize: 10, showSizeChanger: true, pageSizeOptions: [5, 10, 20, 50] },
  className,
  emptyText = __resolve('ui::No data available'),
  // Server-side props
  serverSide = false,
  onSearch,
  onFilter,
  onSort,
  onPaginate,
  totalItems
}: TableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pagination && typeof pagination === 'object' ? (pagination.pageSize || 10) : 10);

  // Filter and search data (only for client-side)
  const filteredData = useMemo(() => {
    if (serverSide) {
      return data; // Server handles filtering
    }

    let result = [...data];

    // Apply search
    if (searchTerm && searchable) {
      const searchableColumns = columns.filter(col => col.searchable !== false);
      result = result.filter(item =>
        searchableColumns.some(col => {
          const value = item[col.key];
          return value && String(value).toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply filters
    Object.entries(activeFilters).forEach(([filterKey, filterValue]) => {
      if (filterValue) {
        result = result.filter(item => String(item[filterKey]) === filterValue);
      }
    });

    // Apply sorting
    if (sortConfig) {
      const sortColumn = columns.find(col => col.key === sortConfig.key);
      result.sort((a, b) => {
        // Use sortKey extractor when provided; otherwise fall back to row[column.key]
        let aVal: unknown = sortColumn?.sortKey ? sortColumn.sortKey(a) : a[sortConfig.key];
        let bVal: unknown = sortColumn?.sortKey ? sortColumn.sortKey(b) : b[sortConfig.key];

        // For numeric string values (e.g. "42"), promote to number for proper numeric sort.
        // Only when there is no sortKey (raw field access). sortKey callers control the type.
        if (!sortColumn?.sortKey) {
          const aNum = (typeof aVal === 'string' && !isNaN(Number(aVal)) && aVal !== '')
            ? Number(aVal)
            : (typeof aVal === 'number' ? aVal : null);
          const bNum = (typeof bVal === 'string' && !isNaN(Number(bVal)) && bVal !== '')
            ? Number(bVal)
            : (typeof bVal === 'number' ? bVal : null);
          if (aNum !== null && bNum !== null) {
            aVal = aNum;
            bVal = bNum;
          }
        }

        // Use shared caseInsensitiveSort (handles nulls, numbers, strings, mixed)
        const comparison = caseInsensitiveSort(aVal, bVal);
        return sortConfig.direction === 'desc' ? comparison * -1 : comparison;
      });
    }

    return result;
  }, [data, searchTerm, activeFilters, sortConfig, columns, searchable, serverSide]);

  // Pagination
  const itemCount = serverSide ? (totalItems || 0) : filteredData.length;
  const totalPages = pagination ? Math.ceil(itemCount / pageSize) : 1;
  const startIndex = pagination ? (currentPage - 1) * pageSize : 0;
  const paginatedData = pagination 
    ? (serverSide ? data : filteredData.slice(startIndex, startIndex + pageSize))
    : (serverSide ? data : filteredData);

  const handleSort = (columnKey: string) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column?.sortable) return;

    const newConfig = (() => {
      if (!sortConfig || sortConfig.key !== columnKey) {
        return { key: columnKey, direction: 'asc' as const };
      }
      if (sortConfig.direction === 'asc') {
        return { key: columnKey, direction: 'desc' as const };
      }
      return null;
    })();

    setSortConfig(newConfig);
    setCurrentPage(1); // Reset to first page when sorting
    
    if (serverSide && onSort && newConfig) {
      onSort(newConfig.key, newConfig.direction);
    }
  };

  const handleFilterChange = (filterKey: string, value: string) => {
    const newFilters = { ...activeFilters, [filterKey]: value };
    setActiveFilters(newFilters);
    setCurrentPage(1);
    
    if (serverSide && onFilter) {
      onFilter(newFilters);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    
    if (serverSide && onSearch) {
      onSearch(value);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    
    if (serverSide && onPaginate) {
      onPaginate(page, pageSize);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    
    if (serverSide && onPaginate) {
      onPaginate(1, newPageSize);
    }
  };

  const renderCell = (column: TableColumn<T>, record: T, index: number) => {
    if (column.render) {
      return column.render(record[column.key], record, index);
    }
    return record[column.key];
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return (
        <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className={cn('w-full space-y-4', className)}>
      {/* Search and Filters */}
      {(searchable || filters.length > 0) && (
        <div className="flex flex-col sm:flex-row gap-4">
          {searchable && (
            <div className="flex-1">
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                }
              />
            </div>
          )}
          
          {filters.map(filter => (
            <div key={filter.key} className="min-w-[200px]">
              <select
                value={activeFilters[filter.key] || ''}
                onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">{filter.label}</option>
                {filter.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-background">
        <div className="overflow-x-auto overflow-y-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={cn(
                      'px-4 py-3 text-left text-sm font-medium text-muted-foreground',
                      column.sortable && 'cursor-pointer hover:text-foreground select-none',
                      column.align === 'center' && 'text-center',
                      column.align === 'right' && 'text-right'
                    )}
                    style={{ width: column.width }}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <div className="flex items-center gap-2">
                      <span>{column.title}</span>
                      {column.sortable && getSortIcon(column.key)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-muted border-t-foreground rounded-full animate-spin" />
                      {__resolve('ui::Loading...')}
                    </div>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">
                    {emptyText}
                  </td>
                </tr>
              ) : (
                paginatedData.map((record, index) => (
                  <tr key={index} className="border-b transition-colors hover:bg-muted/50">
                    {columns.map(column => (
                      <td
                        key={column.key}
                        className={cn(
                          'px-4 py-3 text-sm',
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right',
                          column.key === 'actions' && 'whitespace-nowrap'
                        )}
                      >
                        {renderCell(column, record, startIndex + index)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Info and page size selector */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <div className="text-sm text-muted-foreground">
              {__resolve('ui::Showing {{start}}-{{end}} of {{total}} entries', { start: startIndex + 1, end: Math.min(startIndex + pageSize, itemCount), total: itemCount })}
            </div>
            
            {pagination.showSizeChanger && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">{__resolve('ui::Show:')}</span>
                <select
                  value={String(pageSize)}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="flex h-8 w-16 items-center justify-between rounded-md border border-input bg-background px-2 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  {(pagination.pageSizeOptions || [5, 10, 20, 50]).map(size => (
                    <option key={size} value={String(size)}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {/* Navigation controls */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {/* Page info - show on mobile */}
            <div className="text-sm text-center text-muted-foreground sm:hidden">
              {__resolve('ui::Page {{current}} of {{total}}', { current: currentPage, total: totalPages })}
            </div>
            
            {/* Navigation buttons */}
            <div className="flex items-center justify-center gap-1">
              {/* Desktop: Full navigation */}
              <div className="hidden sm:flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  {__resolve('ui::First')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  {__resolve('ui::Previous')}
                </Button>

                <span className="px-3 py-1 text-sm whitespace-nowrap">
                  {__resolve('ui::Page {{current}} of {{total}}', { current: currentPage, total: totalPages })}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  {__resolve('ui::Next')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  {__resolve('ui::Last')}
                </Button>
              </div>
              
              {/* Mobile: Compact navigation */}
              <div className="flex sm:hidden items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  {__resolve('ui::← Prev')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  {__resolve('ui::Next →')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
